import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaAddressBook,
    FaApple,
    FaChartLine,
    FaChrome,
    FaCoins,
    FaDesktop,
    FaDownload,
    FaExchangeAlt,
    FaGlobe,
    FaGooglePlay,
    FaImages,
    FaLink,
    FaLock,
    FaMobileAlt,
    FaNetworkWired,
    FaRocket,
    FaShieldAlt,
    FaWallet
} from "react-icons/fa";
import { TiArrowUp } from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Wallet = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.2,
            scrollTrigger: {
              trigger: section,
              start: "top 80%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }

    if (scrollTopRef.current) {
      gsap.fromTo(
        scrollTopRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          scrollTrigger: {
            trigger: scrollTopRef.current,
            start: "top bottom",
          },
        }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - 200
    ) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  // Stats
  const stats = [
    { value: "6+", label: "Networks Supported", icon: <FaGlobe /> },
    { value: "100+", label: "Tokens Available", icon: <FaCoins /> },
    { value: "15", label: "Feature Pages", icon: <FaMobileAlt /> },
    { value: "256-bit", label: "Encryption", icon: <FaLock /> },
  ];

  // Core wallet features
  const coreFeatures = [
    {
      title: "Token Swap Exchange",
      description: "Swap tokens instantly without leaving your wallet. Integrated DEX support with real-time price quotes and slippage settings.",
      icon: <FaExchangeAlt />,
      color: "cyan",
    },
    {
      title: "Cross-Chain Bridge",
      description: "Move assets between Ethereum, Polygon, BSC, Avalanche, Arbitrum, and Optimism with built-in bridge functionality.",
      icon: <FaNetworkWired />,
      color: "purple",
    },
    {
      title: "NFT Gallery",
      description: "Display and manage your NFT collection with a beautiful grid layout, floor price tracking, and transfer capabilities.",
      icon: <FaImages />,
      color: "pink",
    },
    {
      title: "DeFi Dashboard",
      description: "Track staking positions, liquidity pools, and yield farming rewards. One-click harvest and unified portfolio view.",
      icon: <FaChartLine />,
      color: "emerald",
    },
    {
      title: "WalletConnect",
      description: "Connect to 1000+ dApps instantly via QR code. Works with Uniswap, OpenSea, Aave, Compound, and more.",
      icon: <FaLink />,
      color: "blue",
    },
    {
      title: "Address Book",
      description: "Save frequent contacts with labels for quick, error-free transactions. Professional-grade contact management.",
      icon: <FaAddressBook />,
      color: "orange",
    },
  ];

  // Security features
  const securityFeatures = [
    "Encrypted seed phrase backup",
    "Biometric authentication ready",
    "Multi-signature support",
    "Auto-lock timer",
    "Token approval management",
    "Hardware wallet compatible",
  ];

  // Supported networks
  const networks = [
    { name: "Ethereum", color: "#627EEA" },
    { name: "Polygon", color: "#8247E5" },
    { name: "BSC", color: "#F0B90B" },
    { name: "Avalanche", color: "#E84142" },
    { name: "Arbitrum", color: "#28A0F0" },
    { name: "Optimism", color: "#FF0420" },
  ];

  // Color schemes
  const colorSchemes = {
    cyan: {
      gradient: "from-cyan-500/20 via-cyan-500/5 to-transparent",
      border: "hover:border-cyan-500/50",
      glow: "group-hover:shadow-cyan-500/25",
      icon: "text-cyan-400",
    },
    purple: {
      gradient: "from-purple-500/20 via-purple-500/5 to-transparent",
      border: "hover:border-purple-500/50",
      glow: "group-hover:shadow-purple-500/25",
      icon: "text-purple-400",
    },
    pink: {
      gradient: "from-pink-500/20 via-pink-500/5 to-transparent",
      border: "hover:border-pink-500/50",
      glow: "group-hover:shadow-pink-500/25",
      icon: "text-pink-400",
    },
    emerald: {
      gradient: "from-emerald-500/20 via-emerald-500/5 to-transparent",
      border: "hover:border-emerald-500/50",
      glow: "group-hover:shadow-emerald-500/25",
      icon: "text-emerald-400",
    },
    blue: {
      gradient: "from-blue-500/20 via-blue-500/5 to-transparent",
      border: "hover:border-blue-500/50",
      glow: "group-hover:shadow-blue-500/25",
      icon: "text-blue-400",
    },
    orange: {
      gradient: "from-orange-500/20 via-orange-500/5 to-transparent",
      border: "hover:border-orange-500/50",
      glow: "group-hover:shadow-orange-500/25",
      icon: "text-orange-400",
    },
  };

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section - Full Viewport Height */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/wallet-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="walletHeroSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.0}
              particleDensity={30}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Zero Crypto Wallet"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Cold-Storage Security · Multi-Chain Support · Instant Swaps
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          additionalClasses=""
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-20 mx-auto max-w-7xl">
        
        {/* Introduction */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              Your Web3 Gateway
            </span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            A production-ready, feature-rich cryptocurrency wallet that rivals MetaMask, Trust Wallet, and Coinbase Wallet.
          </p>
        </motion.div>

        {/* Stats Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative p-6 bg-black/50 backdrop-blur-sm border border-white/10 rounded-2xl text-center group-hover:border-cyan-500/30 transition-all duration-500">
                  <div className="text-cyan-400 text-2xl mb-3 flex justify-center">{stat.icon}</div>
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-300 mt-2">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Download Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20 relative rounded-3xl overflow-hidden"
          ref={(el) => (sectionRefs.current[0] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10" />
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          
          <div className="relative z-10 py-12 px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-sm mb-4">
                  <FaRocket /> Coming Soon to All Platforms
                </div>
                <h3 className="text-3xl font-bold text-white mb-2">Download Zero Wallet</h3>
                <p className="text-gray-300 max-w-md">
                  Get the most advanced crypto wallet on your preferred platform. Currently available as a Chrome extension with mobile apps launching soon.
                </p>
              </div>
              
              <div className="flex flex-wrap justify-center gap-4">
                {/* Chrome Extension - Available */}
                <motion.a
                  href="#"
                  className="flex items-center gap-3 px-6 py-4 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaChrome className="text-2xl" />
                  <div className="text-left">
                    <div className="text-xs opacity-80">Available on</div>
                    <div className="text-sm font-bold">Chrome Web Store</div>
                  </div>
                </motion.a>

                {/* Google Play - Coming Soon */}
                <motion.div
                  className="relative flex items-center gap-3 px-6 py-4 rounded-2xl bg-white/5 border border-white/20 text-gray-300 cursor-not-allowed"
                  whileHover={{ scale: 1.02 }}
                >
                  <FaGooglePlay className="text-2xl" />
                  <div className="text-left">
                    <div className="text-xs opacity-80">Coming Soon</div>
                    <div className="text-sm font-bold">Google Play</div>
                  </div>
                  <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full">
                    SOON
                  </span>
                </motion.div>

                {/* App Store - Coming Soon */}
                <motion.div
                  className="relative flex items-center gap-3 px-6 py-4 rounded-2xl bg-white/5 border border-white/20 text-gray-300 cursor-not-allowed"
                  whileHover={{ scale: 1.02 }}
                >
                  <FaApple className="text-2xl" />
                  <div className="text-left">
                    <div className="text-xs opacity-80">Coming Soon</div>
                    <div className="text-sm font-bold">App Store</div>
                  </div>
                  <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full">
                    SOON
                  </span>
                </motion.div>

                {/* Desktop - Coming Soon */}
                <motion.div
                  className="relative flex items-center gap-3 px-6 py-4 rounded-2xl bg-white/5 border border-white/20 text-gray-300 cursor-not-allowed"
                  whileHover={{ scale: 1.02 }}
                >
                  <FaDesktop className="text-2xl" />
                  <div className="text-left">
                    <div className="text-xs opacity-80">Coming Soon</div>
                    <div className="text-sm font-bold">Desktop App</div>
                  </div>
                  <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full">
                    SOON
                  </span>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Core Features Section */}
        <div className="mb-20" ref={(el) => (sectionRefs.current[1] = el)}>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12"
          >
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Advanced Features
            </span>
          </motion.h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreFeatures.map((feature, index) => {
              const colors = colorSchemes[feature.color];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className={`group relative ${colors.glow} hover:shadow-xl transition-all duration-500`}
                >
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-white/10 via-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute -inset-2 rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500" style={{ background: `linear-gradient(to right, ${colors.gradient})` }} />
                  
                  <div className={`relative h-full bg-black/80 backdrop-blur-sm rounded-2xl p-6 border border-white/10 ${colors.border} transition-all duration-500`}>
                    <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-radial ${colors.gradient} rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    
                    <div className={`relative z-10 w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4 text-xl ${colors.icon} group-hover:scale-110 transition-transform duration-300`}>
                      {feature.icon}
                    </div>
                    
                    <h4 className="relative z-10 text-xl font-bold text-white mb-3">{feature.title}</h4>
                    <p className="relative z-10 text-gray-300 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Networks Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
          ref={(el) => (sectionRefs.current[2] = el)}
        >
          <h3 className="text-3xl font-bold text-center mb-8">
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Multi-Network Support
            </span>
          </h3>
          <p className="text-gray-300 text-center mb-12 max-w-xl mx-auto">
            Access the entire blockchain ecosystem with one-click network switching
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            {networks.map((network, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="group relative"
              >
                <div 
                  className="absolute inset-0 rounded-xl blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-300"
                  style={{ background: network.color }}
                />
                <div 
                  className="relative px-6 py-3 rounded-xl bg-black/50 border border-white/10 group-hover:border-white/30 transition-all duration-300"
                  style={{ borderColor: `${network.color}30` }}
                >
                  <span className="text-white font-medium">{network.name}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Security Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
          ref={(el) => (sectionRefs.current[3] = el)}
        >
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-blue-500/10" />
            <div className="absolute inset-0 bg-black/80" />
            
            <div className="relative z-10 py-16 px-8">
              <div className="flex flex-col md:flex-row items-center gap-12">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm mb-4">
                    <FaShieldAlt /> Bank-Grade Security
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-4">
                    Your Assets, Fully Protected
                  </h3>
                  <p className="text-gray-300 mb-8">
                    Zero Wallet implements the highest security standards with encrypted storage, biometric authentication, and hardware wallet support.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {securityFeatures.map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-center gap-2"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
                
                <div className="flex-shrink-0">
                  <div className="relative w-48 h-48 md:w-64 md:h-64">
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full blur-3xl opacity-20" />
                    <div className="relative w-full h-full bg-black/50 backdrop-blur-sm rounded-3xl border border-white/10 flex items-center justify-center">
                      <FaShieldAlt className="text-7xl md:text-8xl text-emerald-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-3xl overflow-hidden"
          ref={(el) => (sectionRefs.current[4] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20" />
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          <div className="relative z-10 py-16 px-8 text-center">
            <motion.div
              initial={{ scale: 0.9 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-sm mb-6"
            >
              <FaWallet /> Start Your Web3 Journey
            </motion.div>
            
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Take Control of Your Assets?
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto mb-8">
              Join the future of decentralised finance with Zero Wallet. More features than MetaMask, Trust Wallet, and Coinbase Wallet combined.
            </p>
            
            <div className="flex justify-center">
              <GlassButton
                onClick={() => {}}
                variant="cyan"
                size="lg"
                className="px-8 py-4"
              >
                <FaDownload className="mr-2" />
                Get Zero Wallet Now
              </GlassButton>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
          onClick={scrollToTop}
          aria-label="Back to Top"
        >
          <TiArrowUp className="text-cyan-400 transition-all duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider transition-colors duration-500 hover:text-white">
            BACK TO TOP
          </span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Wallet;
